import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import PillarLayout from './PillarLayout';

const CANONICAL = 'https://enbet-consulting.de/ki-strategie-retail';

const faqItems = [
  {
    q: 'Was gehört in eine belastbare KI-Strategie für den Retail?',
    a: 'Vier Elemente, in dieser Reihenfolge: (1) Klare Business-Ziele – KI-Projekte direkt an Umsatzziele, Margen oder Servicekennzahlen koppeln, nicht an Technologie-Interessen. (2) Datenbasis und Governance – verlässliche Daten und klare Regeln für Nutzung, Datenschutz und Qualität. (3) Integrierte Use-Cases – zusammenhängende Wertschöpfungsketten statt isolierter Inselprojekte. (4) Enablement der Fachbereiche – KI-Tools so gestaltet, dass operative Teams sie im Alltag nutzen können. Wer eines dieser Elemente überspringt, fährt die Strategie an die Wand.',
  },
  {
    q: 'Wie kalkuliere ich realistisch den ROI einer KI-Investition?',
    a: 'Drei Hebel werden quantifiziert: Zeitersparnis (Stunden pro Woche × interner Stundensatz × Anzahl betroffener Mitarbeiter), direkte Kosteneinsparung (z. B. weniger Abschriften bei besserer Bestandsprognose), und Umsatzhebel (z. B. höhere Konversion durch Personalisierung). Auf der anderen Seite stehen Einführungskosten (Tool-Lizenzen, Integration, Schulung, Change-Aufwand) und laufende Kosten (API-/Cloud-Gebühren, Monitoring). Eine fundierte ROI-Rechnung gehört in jedes Modul-2-Workshop-Ergebnis bei ENBET – inklusive Sensitivitätsanalyse für konservative und optimistische Annahmen.',
  },
  {
    q: 'Wie läuft der KI Strategie Workshop bei ENBET konkret ab?',
    a: 'Drei Schritte: (1) 1–2 Wochen Ausarbeitung — wir verdichten die Erkenntnisse aus dem Ready Check zu konkreten Hypothesen, Use-Case-Kandidaten und einer vorbereiteten ROI-Logik. (2) Interaktiver Tages-Workshop mit euren Entscheidern (typischerweise 4–6 Personen) — gemeinsame Priorisierung, Festlegung der Roadmap, Quick-Win-Auswahl. (3) Nachbereitung mit finalisiertem Audit-Report, validierter ROI-Kalkulation und 3-Phasen-Plan. Voraussetzung: ein abgeschlossener KI Ready Check (Modul 1, 850 €).',
  },
  {
    q: 'Wie sieht eine pragmatische 3-Phasen-Roadmap aus?',
    a: 'Phase 1 (0–3 Monate): Standort-Check (Modul 1) + Strategie-Workshop (Modul 2) + erster Pilot-Use-Case mit klar definierten Erfolgsmetriken. Phase 2 (3–9 Monate): Pilot stabilisieren, Wirkungsnachweis dokumentieren, Skalierung auf 2–3 weitere Use-Cases. Phase 3 (9–18 Monate): Integration in Kernprozesse, Aufbau interner KI-Kompetenz, kontinuierliches Kosten- und Qualitätsmonitoring. Jede Phase hat klare Go/No-Go-Entscheidungen – ROME-Prinzip: ohne messbares Ergebnis kein Folge-Investment.',
  },
  {
    q: 'Welche Quick-Wins liefern in 4–6 Wochen erste Ergebnisse?',
    a: 'Vier Kategorien funktionieren erfahrungsgemäß als Quick-Win: (1) Automatisierte Auswertung von Kundenfeedback (Bewertungen, E-Mails, Frontline-Notizen) mit Sprachmodellen. (2) KI-gestützte Mitarbeiter-Assistenz für Produktwissen und Service-Standards. (3) Erste Bedarfsprognose-Pilote für 1–2 ABC-Artikel statt für das Gesamtsortiment. (4) Marketing-Automatisierung für E-Mail/Newsletter mit personalisierten Empfehlungen. Jeder dieser Quick-Wins ist in 4–6 Wochen messbar – wenn die Datenbasis vorab geprüft wurde.',
  },
];

const longTailFaq = [
  {
    q: 'Wie unterscheidet sich KI-Strategie von Digitalisierungsstrategie?',
    a: 'Digitalisierung ist Voraussetzung, KI ist Anwendung. Eine Digitalisierungsstrategie sorgt dafür, dass Prozesse erfassbar und Daten verfügbar werden. Eine KI-Strategie nutzt diese Daten, um Vorhersagen zu treffen, Entscheidungen zu unterstützen oder Routinen zu automatisieren. Wer KI-Strategie ohne Digitalisierungsbasis aufsetzt, baut auf Sand.',
  },
  {
    q: 'Was kostet ein KI-Strategie-Workshop bei ENBET?',
    a: 'Der KI Strategie Workshop (Modul 2) kostet 3.500 € netto als Festpreis (zzgl. 19 % MwSt). Enthalten: 1–2 Wochen Ausarbeitung, interaktiver Workshop mit euren Entscheidern (typischerweise 4–6 Personen), validierte ROI-Kalkulation, pragmatische 3-Phasen-Roadmap, konkrete Quick-Wins, strukturierter Audit-Report. Voraussetzung: ein abgeschlossener KI Ready Check (Modul 1, 850 € netto).',
  },
  {
    q: 'Wie sieht eine ROI-Kalkulation für KI-Investitionen konkret aus?',
    a: 'Beispielrechnung: Ein Filialist mit 10 Standorten und 80 Mitarbeitern führt KI-gestützte Schichtplanung ein. Annahme: 2 Stunden Zeitersparnis pro Filialleiter pro Woche × 10 Filialen × 40 € Stundensatz × 50 Wochen = 40.000 € pro Jahr. Einführungskosten einmalig 25.000 €, laufende Kosten 6.000 € pro Jahr. Payback: ca. 9 Monate. Solche Rechnungen sind Standard im KI Strategie Workshop und werden mit echten Daten aus eurem Betrieb belegt.',
  },
];

export default function PillarStrategieRetail() {
  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: 'KI-Strategie Retail – Roadmap, ROI und Quick-Wins',
        description: 'Wie eine belastbare KI-Strategie für den Retail aufgebaut ist: 4 Elemente, 3 Phasen, ROI-Kalkulation und sofort umsetzbare Quick-Wins.',
        author: { '@id': 'https://enbet-consulting.de/#sebastian-funke' },
        publisher: { '@id': 'https://enbet-consulting.de/#organization' },
        mainEntityOfPage: CANONICAL,
        inLanguage: 'de-DE',
        about: 'KI-Strategie Retail',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI im stationären Handel', item: 'https://enbet-consulting.de/ki-im-stationaeren-handel' },
          { '@type': 'ListItem', position: 3, name: 'KI-Strategie Retail', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: [...faqItems, ...longTailFaq].map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="KI-Strategie Retail – Roadmap, ROI und Quick-Wins | ENBET Consulting"
      description="KI-Strategie für den Einzelhandel: 4 Bausteine einer belastbaren Strategie, 3-Phasen-Roadmap, ROI-Kalkulation und sofort umsetzbare Quick-Wins. Boutique-Beratung aus Nordhessen."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      <section className="bg-[#0a0a0a] pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Strategie & Roadmap
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.15] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            KI-Strategie Retail – Roadmap, ROI und Quick-Wins
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Eine KI-Strategie ist nicht das nächste PowerPoint-Dokument im Schrank. Sie ist eine Entscheidung – mit Zahlen, Verantwortlichen und Datum. Dieser Guide zeigt die vier Bausteine, die ROI-Logik und die Quick-Wins, die in 4–6 Wochen messbar werden.
          </motion.p>
        </div>
      </section>

      <section className="bg-[#f7f7f7] py-12 md:py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Kurzantwort
          </p>
          <p className="text-[#1f1f1f] text-lg md:text-xl leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Eine belastbare KI-Strategie im Retail steht auf vier Säulen: klare Business-Ziele, saubere Datenbasis mit Governance, integrierte Use-Cases statt Inselprojekte, und Enablement der Fachbereiche. Sie liefert eine 3-Phasen-Roadmap mit Quick-Wins in 4–6 Wochen und einer ROI-Kalkulation, die für Entscheider belastbar ist. ENBETs Modul 2 (3.500 € Festpreis) liefert genau das – nach einem fundierten Standort-Check.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 space-y-16">
          {faqItems.map((item, i) => (
            <article key={i} className="scroll-mt-24" id={`q-${i + 1}`}>
              <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
                {item.q}
              </h2>
              <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {item.a}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Folgefragen
          </h2>
          <div className="space-y-8">
            {longTailFaq.map((item, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {item.q}
                </h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Weiter vertiefen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { to: '/ki-im-stationaeren-handel', label: 'KI im stationären Handel', sub: 'Grundlagen, Use-Cases und Kosten – der Pillar-Artikel.' },
              { to: '/ki-fuer-filialisten', label: 'KI für Filialisten', sub: 'Multi-Standort-Strategien und Franchise-Logik.' },
              { to: '/ki-beratung-kassel', label: 'KI-Beratung Kassel', sub: 'Lokale Beratung aus Nordhessen für ganz Deutschland.' },
            ].map((link, i) => (
              <Link key={i} to={link.to} className="border border-[#e5e5e5] p-6 hover:border-[#befa0a] transition-colors flex flex-col gap-3">
                <ArrowRight size={18} className="text-[#befa0a]" />
                <span className="text-[#1f1f1f] text-base font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>{link.label}</span>
                <span className="text-[#1f1f1f]/60 text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{link.sub}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0a0a0a] py-16">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-white text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Strategie planen: kostenloser 20-Minuten Potenzial-Check
          </h2>
          <p className="text-white/60 text-base mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Wir klären ehrlich, welcher Use-Case greift, wie eine realistische ROI-Rechnung aussieht – und wo eine Strategie wirklich Sinn macht.
          </p>
          <Link to="/#booking" className="inline-flex items-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors" style={{ fontFamily: "'Oswald', sans-serif" }}>
            <Check size={16} /> Termin vereinbaren
          </Link>
        </div>
      </section>
    </PillarLayout>
  );
}
